import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  Droppable,
  type DroppableProvided,
} from "@hello-pangea/dnd";
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { type VariantProps, tv } from "tailwind-variants";
import { cn } from "../../../../util/classNameConstructor";
import {
  getItemStyle,
  useOnDragEnd,
} from "../../../atoms/sortableList/Sortable";
import InvisibleInput from "../../../core/inputs/InvisibleInput";
import type {
  Question,
  QuestionBaseProps,
  QuestionOption,
} from "../../../special/survey/survey.types";
import { AddButton } from "../components/AddRowButton";
import { ChoiceItem, useChoiceState } from "../components/ChoiceItemWrapper";
import { Row } from "../components/SortableRow";
import { useRandomSorting } from "../hooks";
import { isChecked } from "../utils";
import Textarea from "./Textarea";

const ParticipatoryBudgetStyles = tv({
  slots: {
    Outer: "flex w-full flex-col",
    Wrapper: "w-full pb-6",
    Table: "w-full",
  },
});

interface ParticipatoryBudgetProps
  extends Question,
    VariantProps<typeof ParticipatoryBudgetStyles>,
    QuestionBaseProps {}
interface BudgetProps
  extends Pick<ParticipatoryBudgetProps, "updateQuestion" | "mode"> {
  budget: number;
  spending: number;
}
export const useToPoints = () => {
  const { t, i18n } = useTranslation();
  const formatNumber = new Intl.NumberFormat(i18n.resolvedLanguage, {
    useGrouping: true,
  });
  const toPoints = (val: number) =>
    `${formatNumber.format(val)} ${
      val === 1 ? t("global:point") : t("global:points")
    }`;
  return { toPoints };
};
const useCurrency = (locale: string) => {
  const { i18n } = useTranslation();
  const toCurrency = (
    value: number,
    options = {} as Intl.NumberFormatOptions,
  ) => {
    const currencyOptions = {
      style: "currency",
      currency: "EUR",
      useGrouping: false,
      ...options,
    };
    const formattedValue =
      value?.toLocaleString(i18n.resolvedLanguage, {
        maximumFractionDigits: 2,
        ...currencyOptions,
      }) || "0";

    /**
     * this little transformation is needed to get the correct
     * number of fraction digits with the compact notation
     * e.g. 12300 is 12.3k in english, but 12.300 in german
     * so we need 1 fraction digit in english and 0 in german
     * another example would be 1234567 which is 1.23m in english
     * and 1,23 Mio. in german, so we need 2 fraction digits for both
     */
    const float = Number.parseFloat(
      formattedValue.replace("€", "").replace(",", "."),
    );
    const fraction = String(float).split(".")[1]?.length || 0;

    if (options?.notation === "compact" && fraction > 0) {
      currencyOptions.maximumFractionDigits = fraction;
    }

    return value?.toLocaleString(locale, currencyOptions);
  };

  return { toCurrency };
};
const Budget = ({ budget, spending, updateQuestion, mode }: BudgetProps) => {
  const { t } = useTranslation();
  // const { toCurrency } = useCurrency();
  const { toPoints } = useToPoints();

  const handleChange = ({ target }) => {
    const value = Number.parseInt(
      target.value.replace(/\D/g, "").slice(0, 12),
      10,
    );
    updateQuestion(
      target.value && !Number.isNaN(value) ? value : undefined,
      "budget",
    );
  };
  if (mode === "display") {
    return (
      <h2
        className="mb-3 flex flex-wrap items-baseline gap-x-2"
        aria-live="polite"
      >
        <span className="heading-2xl font-bold">
          {`${toPoints((budget || 0) - (spending || 0))}`}
        </span>
        <span className="text-heading-2xl text-text-subdued font-bold">
          {t("global:remainingValue", {
            val: toPoints(budget || 0),
          })}
        </span>
      </h2>
    );
  }
  return (
    <div className="mb-3 flex flex-wrap items-baseline gap-x-2">
      <InvisibleInput
        size="heading-2xl"
        value={`${budget || ""}`}
        onChange={handleChange}
        placeholder={t("global:existingBudget")}
        mode={mode}
        classNames={{
          container: "-mt-1",
          wrapper: mode !== "edit" ? "w-auto" : "",
        }}
      />
      <span className="text-heading-2xl text-text-subdued font-bold">
        {t("global:remainingValue", {
          val: toPoints(budget || 0),
        })}
      </span>
    </div>
  );
};
const ParticipatoryBudget = ({
  onChange,
  ...props
}: ParticipatoryBudgetProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { Outer, Wrapper, Table } = ParticipatoryBudgetStyles();
  const { t } = useTranslation();
  const type = "checkbox";
  const { toPoints } = useToPoints();
  // const { toCurrency } = useCurrency();

  const handleDragStart = () => {
    ref.current?.scrollTo({ left: 0, behavior: "smooth" });
  };

  const options = useRandomSorting(props, "options", props.mode);

  const spending =
    options?.reduce(
      (acc, curr) =>
        isChecked(props.value, curr.uid, type) ? acc + curr.price : acc,
      0,
    ) || 0;

  const disabled = (option: QuestionOption) => {
    return (
      !isChecked(props.value, option.uid, type) &&
      props.budget - spending < option.price
    );
  };

  return (
    <div className={Outer()}>
      <div className={Wrapper()} ref={ref}>
        {props.mode === "display" ? (
          <>
            <Budget
              budget={props.budget}
              spending={spending}
              updateQuestion={props.updateQuestion}
              mode={props.mode}
            />
            {options.map((option) => (
              <ChoiceItem
                key={option.uid}
                value={props.value}
                onChange={onChange}
                option={option}
                column={props.columns?.[0]}
                question={props}
                classNames={{ label: "justify-start" }}
              />
            ))}
          </>
        ) : (
          <div className={Table()}>
            <Budget
              budget={props.budget}
              spending={spending}
              updateQuestion={props.updateQuestion}
              mode={props.mode}
            />
            <DragDropContext
              onDragEnd={useOnDragEnd(options, (items: QuestionOption[]) =>
                props.updateQuestion(items, "options"),
              )}
              onDragStart={handleDragStart}
            >
              <Droppable droppableId="rows">
                {(droppableProvided: DroppableProvided) => (
                  <div ref={droppableProvided.innerRef} id="pageOptions">
                    {options.map((option, i) => (
                      <Draggable
                        draggableId={option.uid}
                        index={i}
                        key={option.uid}
                        isDragDisabled={props.mode !== "edit"}
                      >
                        {(
                          provided: DraggableProvided,
                          snapshot: DraggableStateSnapshot,
                        ) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getItemStyle(provided, snapshot, "y")}
                            tabIndex={-1}
                          >
                            <Row
                              option={option}
                              rowIndex={i}
                              options={options}
                              {...props}
                              type={type}
                              placeholder={(rowIndex) =>
                                `${t("global:expense")} ${rowIndex + 1}`
                              }
                              secondaryContent={
                                <InvisibleInput
                                  size="body-sm-bold"
                                  value={
                                    props.mode === "edit"
                                      ? `${option.price || ""}`
                                      : toPoints(option.price)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={({ target }) =>
                                    props.updateText(
                                      target.value
                                        ? Number.parseInt(target.value, 10)
                                        : null,
                                      option.uid,
                                      "options",
                                      undefined,
                                      "price",
                                    )
                                  }
                                  textAlign="right"
                                  mode={props.mode}
                                  placeholder={t("global:amount")}
                                  classNames={{
                                    container: "my-0.5 px-3",
                                  }}
                                />
                              }
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {props.otherOption && (
                      <Row
                        option={{ uid: "other", value: t("global:other") }}
                        onChange={onChange}
                        options={options}
                        draggable={false}
                        editable={false}
                        {...props}
                        type={type}
                        rowIndex={options?.length}
                      />
                    )}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}
      </div>
      {props.otherOption &&
        (props.mode === "edit" || isChecked(props.value, "other", type)) && (
          <div className="-mt-4 mb-5">
            <Textarea
              {...props}
              value={props.comment}
              onChange={(comment) => onChange?.({ comment })}
              textRequired={props.textResponseRequired}
              textTitle={
                props.textTitle === undefined
                  ? props.defaultTextTitle
                  : props.textTitle
              }
              placeholder={props.defaultTextTitle}
              maxLength={props.maxChar}
              rows={1}
            />
          </div>
        )}
      <AddButton
        text={t("option")}
        onClick={() => props.addItem("options", { focus: true })}
        className="-mx-0.5"
        classNameWrapper="mr-auto"
        show={props.mode === "edit"}
        id="pageOptions-addButton"
      />
    </div>
  );
};
ParticipatoryBudget.displayName = "ParticipatoryBudget";
export default ParticipatoryBudget;
